import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useRequireAuth, useAuth } from '../../utils/auth'
import { platformApi } from '../../utils/api'
import { ThemeToggle } from '../../components/ThemeContext'
import { getPlatformConfig, getTenantAdminUrl } from '../../utils/config'

interface Tenant {
  id: string
  slug: string
  name: string
  contact_email: string
  plan: string
  status: string
  stripe_onboarded: number
  camps_count: number
  registrations_count: number
  created_at: string
}

export default function PlatformTenants() {
  const navigate = useNavigate()
  const config = getPlatformConfig()
  const { loading, authorized } = useRequireAuth('platform')
  const { logout } = useAuth()
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [loadingTenants, setLoadingTenants] = useState(true)

  useEffect(() => {
    if (!loading && !authorized) {
      navigate('/platform')
    }
  }, [loading, authorized, navigate])

  useEffect(() => {
    if (authorized) {
      loadTenants()
    }
  }, [authorized])

  const loadTenants = async () => {
    try {
      const data = await platformApi.getTenants()
      setTenants(data.tenants || [])
    } catch (err) {
      console.error('Failed to load tenants:', err)
    } finally {
      setLoadingTenants(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
      </div>
    )
  }

  if (!authorized) return null

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21h18M5 21V7l7-4 7 4v14" />
              </svg>
            </div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Platform Admin</h1>
          </div>
          
          <nav className="flex items-center gap-4">
            <Link to="/platform/dashboard" className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition">Dashboard</Link>
            <Link to="/platform/tenants" className="text-gray-900 dark:text-white font-medium">Tenants</Link>
            <ThemeToggle />
            <button onClick={logout} className="btn-ghost text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
              Sign Out
            </button>
          </nav>
        </div>
      </header>
      
      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Tenants</h2>
            <p className="text-gray-500 dark:text-gray-400">Manage all organizations on the platform</p>
          </div>
          <Link to="/platform/tenants/new" className="btn-primary">
            + New Tenant
          </Link>
        </div>
        
        {loadingTenants ? (
          <div className="card p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500 mx-auto" />
          </div>
        ) : tenants.length === 0 ? (
          <div className="card p-12 text-center">
            <div className="text-4xl mb-4">🏢</div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No tenants yet</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">Create your first organization to get started</p>
            <Link to="/platform/tenants/new" className="btn-primary">
              Create Tenant
            </Link>
          </div>
        ) : (
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-800">
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Organization</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Plan</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Status</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Camps</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Registrations</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Stripe</th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((tenant) => (
                  <tr key={tenant.id} className="border-b border-gray-100 dark:border-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800/30">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{tenant.name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{config.platformDomain}/t/{tenant.slug}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`badge ${
                        tenant.plan === 'pro' ? 'badge-info' :
                        tenant.plan === 'starter' ? 'badge-success' :
                        'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}>
                        {tenant.plan}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`badge ${
                        tenant.status === 'active' ? 'badge-success' :
                        tenant.status === 'suspended' ? 'badge-error' :
                        'badge-warning'
                      }`}>
                        {tenant.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-700 dark:text-gray-300">{tenant.camps_count}</td>
                    <td className="px-6 py-4 text-gray-700 dark:text-gray-300">{tenant.registrations_count}</td>
                    <td className="px-6 py-4">
                      {tenant.stripe_onboarded ? (
                        <span className="badge-success">Connected</span>
                      ) : (
                        <span className="badge-warning">Pending</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <a
                        href={getTenantAdminUrl(tenant.slug)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-500 hover:text-brand-400 text-sm"
                      >
                        Open →
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  )
}
